import { Breakpoint, ButtonLevel, ButtonLevels, ButtonSize, ButtonSizes, WindowGlobal } from '../types/globalTypes';
import { EventTypes, environment, version } from '../global/AppInsights/constants';

export type ShadowRootLegacy = ShadowRoot & { tagName: string };

/**
 * Generates a unique component ID
 * @param   {string}  namespace   The unique id's namespace (e.g. ads)
 * @returns {string}              A generated random id (e.g. ads-1234)
 */
export const generateId = (namespace = 'ads'): string => {
  return `${namespace}-${generateRandomString()}`;
};

/**
 * Generates a randon string
 * @returns {string}              A generated random string
 */
export const generateRandomString = (): string => {
  return Math.random().toString(36).substr(2, 16);
};

/**
 * Generates a size class based on a button size
 * @param {ButtonSize}  size    The size of the button (e.g. ButtonSizes.SMALL)
 * @returns {string}            The button size class (e.g. 'is-small')
 */
export const getClassFromSize = (size: ButtonSize): string => {
  switch (size) {
    case ButtonSizes.MEDIUM:
      return 'is-medium';
    case ButtonSizes.SMALL:
      return 'is-small';
    default:
      return '';
  }
};

/**
 * Generates a level class based on button a level
 * @param {ButtonLevel} level   The level of the button (e.g. ButtonLevels.PRIMARY)
 * @returns {string}            The button level class (e.g. 'is-primary')
 */
export const getClassFromLevel = (level: ButtonLevel): string => {
  switch (level) {
    case ButtonLevels.PRIMARY:
      return 'is-primary';
    case ButtonLevels.SECONDARY:
      return 'is-secondary';
    case ButtonLevels.TERTIARY:
      return 'is-tertiary';
    default:
      return '';
  }
};

/**
 * Given a breakpoint it will return the appropriate class for the media-queries (breakpoints-up).
 * @param {Breakpoint} bp The breakpoint.
 * @returns {string} The class.
 */
export const getMediaQueryBreakpointClassUp = (bp: Breakpoint): string => `${bp.toString()}-up`;

/**
 * Given a breakpoint it will return the appropriate class for the media-queries (breakpoints-down).
 * @param {Breakpoint} bp The breakpoint.
 * @returns {string} The class.
 */
export const getMediaQueryBreakpointClassDown = (bp: Breakpoint): string => `${bp.toString()}-down`;

/**
 * Display an ADS warning with the given message
 * @param {string}  message       The error message to show
 * @param {string}  component     The reference to the component
 * @returns {void}
 */
export const showErrorMessage = (message: string, component: HTMLElement): void => {
  try {
    (window as WindowGlobal)['AppInsights'].trackEvent(
      {
        name: EventTypes.ADS_CONSOLE_ERROR as string,
      },
      {
        component: component.tagName.toLowerCase(),
        componentId: component.id,
        message: message,
        environment,
        version,
        host: window.location.host,
      }
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('[APP INSIGHTS] Could not send console error', err);
  }
  // eslint-disable-next-line no-console
  console.warn(`ADS-Warning: ${message}`);
};
